// Pure helpers for the Links page (CONTEXT.md "Links puzzle").

export const utcToday = () => new Date().toISOString().slice(0, 10);

const addDays = (isoDate, days) => {
    const d = new Date(`${isoDate}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
};

const firstDayNotIn = (taken, today) => {
    let day = today;
    while (taken.has(day)) day = addDays(day, 1);
    return day;
};

const approvedDates = (puzzles) => puzzles.filter(p => p.status === 'approved').map(p => p.date);

// The first day from `today` (UTC, YYYY-MM-DD) with no scheduled puzzle.
export function nextFreeDate(puzzles, today = utcToday()) {
    return firstDayNotIn(new Set(approvedDates(puzzles)), today);
}

// The first day with nothing on it yet: no scheduled puzzle, no dated
// draft, no generation running.
export function nextOpenDate(puzzles, generations, today = utcToday()) {
    const taken = new Set([
        ...puzzles.filter(p => p.date).map(p => p.date),
        ...generations.filter(g => g.status === 'running').map(g => g.date),
    ]);
    return firstDayNotIn(taken, today);
}

// Why `date` cannot be generated for, or '' when it can.
export function generateBlocker(date, puzzles, generations, today = utcToday()) {
    if (!date) return 'Pick a date.';
    if (date < today) return 'The date is in the past.';
    if (approvedDates(puzzles).includes(date)) return `${date} already has a scheduled puzzle.`;
    if (generations.some(g => g.date === date && g.status === 'running')) return `${date} is already being generated.`;
    return '';
}

export const LEVELS = [
    { value: 'easy', label: 'Easy' },
    { value: 'normal', label: 'Normal' },
    { value: 'hard', label: 'Hard' },
];

export const levelLabel = (level) => LEVELS.find(l => l.value === level)?.label || level;

export const isGenerating = (generations) => generations.some(g => g.status === 'running');

// " · #12" after a numbered puzzle's date or status; shared by the pill and the list.
export const numberSuffix = (puzzle) => (puzzle.number ? ` · #${puzzle.number}` : '');

// The status pill: "Scheduled 2026-10-12 · #12", "Draft for 2026-10-12".
export function statusLabel(puzzle) {
    const number = numberSuffix(puzzle);
    if (puzzle.status === 'approved') return `Scheduled ${puzzle.date}${number}`;
    return `Draft${puzzle.date ? ` for ${puzzle.date}` : ''}${number}`;
}

// Another puzzle with the same number, if any: worth a warning, not a block.
export const numberClash = (puzzle, puzzles) =>
    (puzzle.number ? puzzles.find(p => p.id !== puzzle.id && p.number === puzzle.number) : undefined);

// The banner text when fewer than `target` consecutive days are scheduled.
export function bufferWarning(buffer, target) {
    if (buffer >= target) return '';
    const days = buffer === 1 ? '1 day' : `${buffer} days`;
    return `Only ${days} of Links puzzles scheduled from today (target: ${target}). Generate or approve drafts.`;
}

export const themes = (puzzle) => puzzle.groups.map(g => g.theme).join(' · ');

// A scheduled puzzle whose day has gone by. A draft is never past, dated
// or not: it can still be approved for another day or deleted.
export const isPast = (puzzle, today = utcToday()) => puzzle.status === 'approved' && puzzle.date < today;

export const tabOf = (puzzle, today = utcToday()) => (isPast(puzzle, today) ? 'past' : 'upcoming');

const byDate = (a, b) => a.date.localeCompare(b.date);

// The list's two tabs. Upcoming leads with the scheduled puzzles by date,
// so today's board opens by default, then the drafts in the API's order
// (dated ones by date, then undated newest first); past is newest first.
export function splitPuzzles(puzzles, today = utcToday()) {
    const scheduled = puzzles.filter(p => p.status === 'approved' && !isPast(p, today)).sort(byDate);
    const drafts = puzzles.filter(p => p.status !== 'approved');
    const past = puzzles.filter(p => isPast(p, today)).sort((a, b) => byDate(b, a));
    return { upcoming: [...scheduled, ...drafts], past };
}

export const warningsOf = (puzzle) => puzzle.scores?.warnings || [];

// The site an off-course term's source is on, for its review link.
export function sourceHost(url) {
    try {
        return new URL(url).hostname.replace(/^www\./, '');
    } catch {
        return url;
    }
}

// Terms carry soft hyphens (U+00AD) where the app may break a long word.
// Editors type them as `~`; previews show them as `·`.
const SOFT_HYPHEN = '\u00ad';
export const SOFT_HYPHEN_KEY = '~';

export const plainTerm = (term) => term.replaceAll(SOFT_HYPHEN, '');

// The term as the app's tile shows it, with its break points visible.
export const tileText = (term) => term.toUpperCase().replaceAll(SOFT_HYPHEN, '·');

// Deep copy of the editable board for the edit form, soft hyphens as `~`.
export const boardToForm = (puzzle) => puzzle.groups.map(g => ({
    ...g, terms: g.terms.map(t => ({ ...t, term: t.term.replaceAll(SOFT_HYPHEN, SOFT_HYPHEN_KEY) })),
}));

// The form back into the board the API takes.
export const formToBoard = (groups) => groups.map(g => ({
    ...g, terms: g.terms.map(t => ({ ...t, term: t.term.replaceAll(SOFT_HYPHEN_KEY, SOFT_HYPHEN) })),
}));
